import React, { useMemo, useState } from "react";
import { toMapEmbedUrl } from "@/lib/maps/googleMaps";
import { FALLBACK_BRANCHES } from "@/lib/api/branches";

const ContactCards = ({ branches }) => {
  const offices = useMemo(
    () =>
      Array.isArray(branches) && branches.length > 0
        ? branches
        : FALLBACK_BRANCHES,
    [branches]
  );

  // Head office is the pin the map opens on
  const defaultIndex = Math.max(
    offices.findIndex((office) => office.isHQ),
    0
  );
  const [activeIndex, setActiveIndex] = useState(defaultIndex);

  const active = offices[activeIndex] || offices[0];
  const embedUrl = active ? toMapEmbedUrl(active) : null;
  // To re-enable the "Get directions" button below, restore:
  //   import { toDirectionsUrl } from "@/lib/maps/googleMaps";
  //   const directionsUrl = active ? toDirectionsUrl(active) : null;

  return (
    <div className="container mt--40 mt-lg--60 mb--40 mb-lg--60">
      <h1 className="title h2 mb--30 mb-lg--40">Contact us</h1>

      {/* Names the office grid for crawlers and screen readers without altering
          the visible design. It also keeps the outline valid: without an h2
          here, the office cards would sit between the h1 and the enquiry
          form's h2, which reads as a broken heading sequence. */}
      <h2 className="visually-hidden">Our Offices</h2>

      <div className="row g-4">
        {offices.map((office, index) => {
          const isActive = index === activeIndex;
          const mappable = !!toMapEmbedUrl(office);

          return (
            <div className="col-12 col-md-6 col-lg-4" key={office.id ?? index}>
              <div
                onClick={() => mappable && setActiveIndex(index)}
                onKeyDown={(event) => {
                  if (!mappable) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setActiveIndex(index);
                  }
                }}
                role={mappable ? "button" : undefined}
                tabIndex={mappable ? 0 : undefined}
                aria-pressed={mappable ? isActive : undefined}
                style={{
                  border: `1px solid ${
                    isActive ? "var(--color-primary)" : "var(--color-border)"
                  }`,
                  boxShadow: isActive
                    ? "0 0 0 1px var(--color-primary)"
                    : "none",
                  borderRadius: "16px",
                  padding: "20px 16px",
                  backgroundColor: "var(--color-white)",
                  height: "100%",
                  cursor: mappable ? "pointer" : "default",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                }}
              >
                <div className="mb--20">
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill="var(--color-primary)"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5 14.5 7.62 14.5 9 13.38 11.5 12 11.5z" />
                  </svg>
                </div>
                <h3 className="h5 mb--10">{office.name}</h3>
                {office.address && (
                  <p className="b3 mb--15" style={{ lineHeight: "1.6" }}>
                    {office.address}
                  </p>
                )}
                {office.phone && (
                  <p
                    className="b3"
                    style={{
                      marginBottom: 0,
                      color: "var(--color-heading)",
                    }}
                  >
                    Call Now:{" "}
                    <a
                      href={`tel:${office.phone.replace(/[^\d+]/g, "")}`}
                      onClick={(event) => event.stopPropagation()}
                      style={{
                        color: "var(--color-primary)",
                        textDecoration: "none",
                      }}
                    >
                      {office.phone}
                    </a>
                  </p>
                )}
                {mappable && (
                  <p
                    className="b3"
                    style={{
                      marginTop: "12px",
                      marginBottom: 0,
                      color: isActive
                        ? "var(--color-primary)"
                        : "var(--color-body)",
                    }}
                  >
                    {isActive ? "Showing on map" : "View on map"}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ---------------- Map ---------------- */}
      {embedUrl && (
        <div className="mt--40 mt-lg--50">
          <div className="contact-map">
            <iframe
              key={embedUrl}
              src={embedUrl}
              title={`Map showing ${active.name}`}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* <div className="contact-map__footer">
            <div>
              <strong>{active.name}</strong>
              {active.address && <div className="b3">{active.address}</div>}
            </div>
            {directionsUrl && (
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-map__directions"
              >
                Get directions
              </a>
            )}
          </div> */}
        </div>
      )}

      <style jsx>{`
        /* Fixed ratio rather than fixed height so the map scales cleanly */
        .contact-map {
          position: relative;
          width: 100%;
          aspect-ratio: 21 / 9;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid var(--color-border);
        }

        .contact-map :global(iframe) {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: 0;
        }

        .contact-map__footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 16px;
        }

        .contact-map__directions {
          padding: 10px 18px;
          border-radius: 8px;
          background-color: var(--color-primary);
          color: #fff;
          text-decoration: none;
          font-size: 14px;
          white-space: nowrap;
        }

        @media (max-width: 991px) {
          .contact-map {
            aspect-ratio: 4 / 3;
          }
        }

        @media (max-width: 575px) {
          .contact-map {
            aspect-ratio: 1 / 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ContactCards;
